/*
 * Page-view and tool-usage beacon.
 *
 * Fire and forget. It must never block first paint, never surface an error, and
 * never matter if it fails. The editor and the agent tools work exactly the same
 * whether or not any of this goes through.
 *
 * Nothing identifying leaves the browser: no id, no cookie, no storage, no user
 * agent. Three things are sent:
 *
 *   view             once on load, with the route name from a fixed list
 *   tools_first_use  the first time an agent calls any tool on this page, with
 *                    the transport so native ChatGPT can be told apart from a
 *                    polyfilled browser
 *   session_summary  per-tool call counts, repeatedly while the page lives
 *
 * The summary deliberately does NOT wait for the page to be closed. Nobody
 * closes an editor tab, and in an embedded browser like ChatGPT Desktop's there
 * is no guarantee pagehide ever fires. So it reports on a timer, sending only
 * the calls counted since the last report. The server $incs, so the parts add
 * up to the whole and a lost report costs only its own delta.
 *
 * That needs an id to add the parts of one page load together, so each load
 * gets a random one. It is generated in memory, never stored, never sent
 * anywhere else and gone when the page is. It cannot be joined to anything
 * before or after that page load, which is the property that matters.
 *
 * The endpoint is same-origin. App Builder serves Runtime web actions under
 * /api/v1/web on the same host as the static site, so there is no CORS preflight
 * and no hard-coded environment URL.
 */

const ENDPOINT = '/api/v1/web/minipaint-webmcp/beacon'

/** Only calls that came from a real agent count as "someone used the MCP". */
const AGENT_SOURCE = 'webmcp'

/** How often unreported tool calls are sent. Short enough that an abandoned tab
 *  still yields its numbers, long enough not to be chatty. */
const FLUSH_INTERVAL_MS = 15000

/**
 * A random id for this page load. Not a visitor id: it exists only in memory,
 * never touches storage or a cookie, and dies with the page. Its only job is to
 * let repeated reports from one page load accumulate into one document.
 */
function newSessionId (win) {
  const crypto = win && win.crypto
  if (crypto && typeof crypto.getRandomValues === 'function') {
    const bytes = new Uint8Array(8)
    crypto.getRandomValues(bytes)
    return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
  }
  return (Math.random().toString(16).slice(2) + '0'.repeat(16)).slice(0, 16)
}

/** Hostnames where counting would only pollute the numbers. */
function isCountableHost (hostname) {
  return !(
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname === '' ||
    hostname.endsWith('.local')
  )
}

/** Map the hash route onto the small set the server is willing to record. */
export function routeName (hash) {
  const path = String(hash || '').replace(/^#/, '')
  if (path.startsWith('/webmcp-debug')) return 'webmcp-debug'
  if (path.startsWith('/editor') || path === '/' || path === '') return 'editor'
  return 'other'
}

/**
 * Post one event. Resolves to true when the request was sent, false when it was
 * skipped or failed. Never rejects.
 */
export async function send (payload, { win = typeof window !== 'undefined' ? window : undefined } = {}) {
  try {
    if (!win || !win.location || typeof win.fetch !== 'function') return false
    if (!isCountableHost(win.location.hostname)) return false

    await win.fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      // Survives the page being closed straight after the call.
      keepalive: true
    })
    return true
  } catch (e) {
    // Deliberately silent. A counter is not worth a console error on a user's page.
    return false
  }
}

/**
 * Count this page view.
 *
 * `transport` says which WebMCP implementation the browser turned out to have.
 * 'native' means ChatGPT's built-in browser, 'polyfill' means an ordinary one.
 * That single field is what separates "someone looked at it" from "someone
 * opened it in the client this was built for".
 */
export async function sendBeacon ({ transport = 'unknown', win = typeof window !== 'undefined' ? window : undefined } = {}) {
  if (!win || !win.location) return false
  return send({ event: 'view', route: routeName(win.location.hash), transport }, { win })
}

/**
 * Watch the tool registry and report agent usage.
 *
 * Two reports, for two different reliability needs. The first-use ping goes out
 * immediately, because "did anyone actually drive this with an agent" is the
 * number worth trusting. The per-tool counts then go out on a timer, each report
 * carrying only what happened since the last one.
 *
 * @param {object} deps
 * @param {{subscribe: Function, invocations: Function}} deps.registry
 * @param {{status: Function}} deps.adapter
 * @param {Window} [deps.win]
 * @param {number} [deps.intervalMs]
 * @returns {Function} unsubscribe
 */
export function trackToolUsage ({
  registry,
  adapter,
  win = typeof window !== 'undefined' ? window : undefined,
  intervalMs = FLUSH_INTERVAL_MS
}) {
  if (!registry || typeof registry.subscribe !== 'function') return () => {}

  const sessionId = newSessionId(win)
  // Calls counted but not yet reported. Emptied by every successful flush, so a
  // report is always a delta and the server's $inc adds up to the true total.
  const pending = {}
  let reportedFirstUse = false
  let lastSeenId = 0
  let timer = null

  const transport = () => {
    try {
      return adapter && typeof adapter.status === 'function' ? adapter.status().transport : 'unknown'
    } catch (e) {
      return 'unknown'
    }
  }

  const flush = () => {
    const tools = { ...pending }
    if (!Object.keys(tools).length) return false
    for (const name of Object.keys(pending)) delete pending[name]
    send({
      event: 'session_summary',
      route: win && win.location ? routeName(win.location.hash) : 'other',
      transport: transport(),
      sessionId,
      tools
    }, { win })
    return true
  }

  // The timer only runs while there is something to report, so an idle page
  // costs nothing.
  const startTimer = () => {
    if (timer !== null || !win || typeof win.setInterval !== 'function') return
    timer = win.setInterval(() => {
      if (!flush()) stopTimer()
    }, intervalMs)
  }

  const stopTimer = () => {
    if (timer === null) return
    if (win && typeof win.clearInterval === 'function') win.clearInterval(timer)
    timer = null
  }

  const offRegistry = registry.subscribe(() => {
    let invocations
    try {
      invocations = registry.invocations()
    } catch (e) {
      return
    }
    // Newest first, and each entry has a monotonic id, so only look at new ones.
    let counted = false
    for (const entry of invocations) {
      if (entry.id <= lastSeenId) break
      if (entry.source !== AGENT_SOURCE) continue
      pending[entry.tool] = (pending[entry.tool] || 0) + 1
      counted = true
    }
    if (invocations.length) lastSeenId = Math.max(lastSeenId, invocations[0].id)
    if (!counted) return

    if (!reportedFirstUse) {
      reportedFirstUse = true
      send({ event: 'tools_first_use', transport: transport() }, { win })
    }
    startTimer()
  })

  // Still worth listening for: when the page does go away, the last few calls
  // should not wait for a timer that will never fire again.
  if (win && typeof win.addEventListener === 'function') {
    win.addEventListener('pagehide', flush)
    win.addEventListener('visibilitychange', () => {
      if (win.document && win.document.visibilityState === 'hidden') flush()
    })
  }

  return () => {
    offRegistry()
    stopTimer()
    flush()
  }
}
